import type {
  BadgeValue,
  ComparisonCellValue,
  ComparisonItem,
  ComparisonRowDef,
} from '@highereducation/hen-ui';
import type { ResultsPageComparisonConfig } from '@de/types/types';
import type { Result } from '../../types/result';
import { isAccreditationHidden } from '../../utils/accreditationVisibility';
import { interpolate } from '../../utils/interpolation/interpolate';
import { sanitizeHtmlString } from '../../shared/utils/parseHtmlString';
import {
  resolveActiveProductFlags,
  type ProductFlagDictionaryKey,
} from '../ProductFlags/productFlagsResolve';
import type { ProductFlagsProps } from '../ProductFlags/types';

const MISSING_CONFIGURED_VALUE = 'N/A';

export const DEFAULT_COMPARISON_ROW_DEFS: ComparisonRowDef[] = [
  {
    key: 'flags',
    label: 'Highlights',
    type: 'badgeList',
    badgeVariant: 'neutral',
    badgeSize: 'sm',
  },
  {
    key: 'onlineEnrollment',
    label: 'Online Enrollment',
    type: 'text',
    labelTooltip: 'Percentage of all students who are enrolled online.',
  },
  {
    key: 'accreditation',
    label: 'Accreditation',
    type: 'text',
    // Sticky row labels are shared across columns, so tooltip copy is
    // static; accreditor presence stays in the Yes/N/A cell value.
    labelTooltip: "The school's accrediting agency or organization.",
  },
  { key: 'onlinePrograms', label: 'Online Programs', type: 'text' },
  { key: 'retentionRate', label: 'Retention Rate', type: 'text' },
  { key: 'transferCredits', label: 'Transfer Credits Accepted', type: 'text' },
  { key: 'feeWaived', label: 'Online Application Fee Waived', type: 'text' },
  { key: 'satAct', label: 'SAT/ACT Required', type: 'text' },
  { key: 'snippet', label: 'Program Overview', type: 'text' },
  {
    key: 'tcpa',
    label: 'Terms and Conditions',
    type: 'text',
    // Legal disclosure: full copy, no line-clamp / Read more.
    clamp: false,
    html: true,
  },
];

export const COMPARISON_ROW_DEFS = DEFAULT_COMPARISON_ROW_DEFS;

export type ComparisonMappingDeps = {
  onProductClick: (result: Result) => void;
  onRemove: (programId: number) => void;
  isProgramSubmitted: (programId: number) => boolean;
  config?: ResultsPageComparisonConfig;
};

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A';
  return `${Math.round(Number(value))}%`;
}

export function formatYesNo(value: boolean | undefined): string {
  if (value === undefined) return 'N/A';
  return value ? 'Yes' : 'No';
}

/**
 * Pulls the subset of `Result` properties consumed by `ProductFlagsDictionary`.
 * Keeps mapping logic explicit instead of spreading the full result into the
 * resolver (the resolver tolerates extra keys but the contract is clearer here).
 */
export function productFlagsFromResult(result: Result): ProductFlagsProps {
  return {
    isOnline: result.isOnline,
    isPublic: result.isPublic,
    isPrivate: result.isPrivate,
    forProfit: result.forProfit,
    notForProfit: result.notForProfit,
  };
}

function ctaLabelFor(
  result: Result,
  submitted: boolean,
  configuredLabel?: string,
): string {
  if (submitted) return 'Next Steps';
  if (configuredLabel) return configuredLabel;
  if (result.applicationCtaLabel) return result.applicationCtaLabel;
  return 'Learn More';
}

function flagBadges(result: Result): BadgeValue[] {
  return resolveActiveProductFlags(productFlagsFromResult(result)).map(
    ({ flag }: { key: ProductFlagDictionaryKey; flag: { value: string } }) => ({
      text: flag.value,
    }),
  );
}

export function comparisonRowDefsForConfig(
  config?: ResultsPageComparisonConfig,
): ComparisonRowDef[] {
  const attributes = config?.compareAttributes;
  if (!attributes?.length) {
    return DEFAULT_COMPARISON_ROW_DEFS;
  }
  return attributes.map((attribute, index) => ({
    key: comparisonAttributeKey(attribute.key, index),
    label: attribute.label,
    type: 'text',
  }));
}

export function resultsToComparisonItems(
  results: Result[],
  {
    onProductClick,
    onRemove,
    isProgramSubmitted,
    config,
  }: ComparisonMappingDeps,
): ComparisonItem[] {
  const configuredAttributes = config?.compareAttributes ?? [];
  const configuredButtonText = config?.buttonText?.trim();
  return results.map((result) => {
    const submitted = isProgramSubmitted(result.programId);
    const label = ctaLabelFor(result, submitted, configuredButtonText);

    return {
      id: String(result.programId),
      header: {
        image: {
          src: result.imageURL ?? '',
          alt: `${result.schoolName} logo`,
        },
        title: result.schoolName,
        subtext: result.programName,
        remove: {
          onClick: () => onRemove(result.programId),
          ariaLabel: `Remove ${result.schoolName} from comparison`,
        },
      },
      ctaBottom: {
        label,
        onClick: () => onProductClick(result),
      },
      fields: {
        flags: { badges: flagBadges(result) },
        onlineEnrollment: {
          text: formatPercent(result.percentOnlineEnrollment),
        },
        accreditation: {
          text:
            result.accreditator &&
            !isAccreditationHidden(result.institutionalStats)
              ? 'Yes'
              : 'N/A',
        },
        onlinePrograms: {
          text: result.numberOnlinePrograms
            ? String(result.numberOnlinePrograms)
            : 'N/A',
        },
        retentionRate: { text: formatPercent(result.fulltimeRetentionRate) },
        transferCredits: {
          text: formatYesNo(result.areTransferCreditsAccepted),
        },
        feeWaived: { text: formatYesNo(result.isApplicationFeeOnlineWaived) },
        satAct: { text: result.satOrActAdmissionsRequirements ?? 'N/A' },
        snippet: { text: result.snippet ?? '' },
        tcpa: {
          // Pre-sanitize for TextCell `html: true` (CMS/marketing allowlist).
          text: result.tcpa?.trim() ? sanitizeHtmlString(result.tcpa) : 'N/A',
        },
        ...configuredComparisonFields(result, configuredAttributes),
      } satisfies Record<string, ComparisonCellValue>,
    };
  });
}

function configuredComparisonFields(
  result: Result,
  attributes: NonNullable<ResultsPageComparisonConfig['compareAttributes']>,
): Record<string, ComparisonCellValue> {
  return attributes.reduce<Record<string, ComparisonCellValue>>(
    (fields, attribute, index) => {
      fields[comparisonAttributeKey(attribute.key, index)] = {
        text: resolveConfiguredValue(result, attribute.key),
      };
      return fields;
    },
    {},
  );
}

function comparisonAttributeKey(key: string, index: number): string {
  return `configured-${index}-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

function resolveConfiguredValue(result: Result, key: string): string {
  const extracted = result.compareAttributeValues?.find(
    (attribute) => attribute.key === key || attribute.label === key,
  );
  if (extracted?.value) {
    return extracted.value;
  }
  const template = key.includes('{{')
    ? key
    : key.startsWith('item.')
      ? `{{${key}}}`
      : `{{item.${key}}}`;
  try {
    const resolved = interpolate(template, { item: result });
    if (resolved === null || resolved === undefined || resolved === '') {
      return MISSING_CONFIGURED_VALUE;
    }
    if (Array.isArray(resolved)) {
      return resolved.length > 0
        ? resolved.map((value) => String(value)).join(', ')
        : MISSING_CONFIGURED_VALUE;
    }
    if (typeof resolved === 'object') {
      return JSON.stringify(resolved) ?? MISSING_CONFIGURED_VALUE;
    }
    return String(resolved);
  } catch {
    return MISSING_CONFIGURED_VALUE;
  }
}
